/**
 * Checks the checkout delivery postcode is a real UK postcode (DEV-51047 AC3).
 *
 * Only a postcode the engineer has changed is checked: the working postcode
 * was already checked when they set it. The check must never put order
 * placement at risk, so only a definite "this postcode does not exist" from
 * postcodes.io blocks Place order. When postcodes.io can't be reached, the
 * address-list service is asked instead; if neither confirms the postcode,
 * the result is `unchecked` and the order is still allowed.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../services/api'
import { checkPostcode, normalizePostcode } from '../services/postcodes'
import type { PostcodeCoordinates } from '../services/postcodes'

// Wait after the last keystroke before checking, so we don't check every
// half-typed postcode.
const CHECK_DEBOUNCE_MS = 600

// Longest Place order will wait for a check still in flight. After this the
// order goes ahead as `unchecked` rather than keep the engineer waiting.
const SETTLE_TIMEOUT_MS = 8000

export type DeliveryPostcodeStatus =
  | 'idle' // empty, or the same as the working postcode
  | 'checking'
  | 'found'
  | 'not_found'
  | 'unchecked' // no service could confirm it either way

export interface DeliveryPostcodeResult {
  status: DeliveryPostcodeStatus
  /** Canonical spelling ("SO16 0AS") when found. */
  postcode: string | null
  /** Postcode centre when found via postcodes.io; null otherwise. */
  coordinates: PostcodeCoordinates | null
}

const IDLE_RESULT: DeliveryPostcodeResult = { status: 'idle', postcode: null, coordinates: null }
const CHECKING_RESULT: DeliveryPostcodeResult = {
  status: 'checking',
  postcode: null,
  coordinates: null,
}
const UNCHECKED_RESULT: DeliveryPostcodeResult = {
  status: 'unchecked',
  postcode: null,
  coordinates: null,
}

/** Ask the address-list service whether it knows the postcode. Never throws. */
async function confirmViaAddressList(postcode: string): Promise<DeliveryPostcodeResult> {
  try {
    const response = await api.lookupAddress(postcode)
    if (response.success && response.data && response.data.addresses.length > 0) {
      return {
        status: 'found',
        postcode: response.data.postcode || postcode.trim().toUpperCase(),
        coordinates: null,
      }
    }
  } catch {
    // Fall through: an error here must not block the order.
  }
  return UNCHECKED_RESULT
}

async function runCheck(postcode: string): Promise<DeliveryPostcodeResult> {
  const outcome = await checkPostcode(postcode)
  if (outcome.kind === 'found') {
    return {
      status: 'found',
      postcode: outcome.resolved.postcode,
      coordinates: {
        latitude: outcome.resolved.latitude,
        longitude: outcome.resolved.longitude,
      },
    }
  }
  if (outcome.kind === 'not_found') {
    return { status: 'not_found', postcode: null, coordinates: null }
  }
  return confirmViaAddressList(postcode)
}

function withTimeout(check: Promise<DeliveryPostcodeResult>): Promise<DeliveryPostcodeResult> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(UNCHECKED_RESULT), SETTLE_TIMEOUT_MS)
    check
      .then(resolve)
      .catch(() => resolve(UNCHECKED_RESULT))
      .finally(() => clearTimeout(timer))
  })
}

export function useDeliveryPostcodeCheck(deliveryPostcode: string, workingPostcode: string) {
  const normalized = normalizePostcode(deliveryPostcode)
  const needsCheck = normalized !== '' && normalized !== normalizePostcode(workingPostcode)

  // The last answer, tagged with the postcode it is for. An answer for a
  // postcode the engineer has since changed is simply ignored below.
  const [answer, setAnswer] = useState<{ key: string; result: DeliveryPostcodeResult } | null>(
    null,
  )
  // One request per distinct postcode, shared by the debounced check and
  // Place order so the two never race each other.
  const checksRef = useRef(new Map<string, Promise<DeliveryPostcodeResult>>())

  const startCheck = useCallback((key: string): Promise<DeliveryPostcodeResult> => {
    let check = checksRef.current.get(key)
    if (!check) {
      check = runCheck(key)
      checksRef.current.set(key, check)
      // A failed answer may succeed on retry, so don't keep it.
      void check.then((outcome) => {
        if (outcome.status === 'unchecked') checksRef.current.delete(key)
      })
    }
    void check.then((outcome) => setAnswer({ key, result: outcome }))
    return check
  }, [])

  useEffect(() => {
    if (!needsCheck) return
    const timer = setTimeout(() => void startCheck(normalized), CHECK_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [normalized, needsCheck, startCheck])

  const result: DeliveryPostcodeResult = !needsCheck
    ? IDLE_RESULT
    : answer?.key === normalized
      ? answer.result
      : CHECKING_RESULT

  /**
   * Finish any pending check now (skipping the debounce) for Place order.
   * Resolves within SETTLE_TIMEOUT_MS and never rejects.
   */
  const settle = (): Promise<DeliveryPostcodeResult> => {
    if (!needsCheck) return Promise.resolve(IDLE_RESULT)
    return withTimeout(startCheck(normalized))
  }

  return { ...result, settle }
}
